import { humanize } from './humanize';

function flattenArtifacts(paper, path) {
  const artifacts = paper.contribution?.artifacts || [];
  const values = [];
  artifacts.forEach((artifact) => {
    let cur = artifact;
    for (const key of path) {
      if (!cur || typeof cur !== 'object') {
        cur = undefined;
        break;
      }
      cur = cur[key];
    }
    if (Array.isArray(cur)) values.push(...cur);
  });
  return [...new Set(values)];
}

export function filterArtifactTypes(types) {
  return types;
}

export function getArtifactTypes(paper) {
  return filterArtifactTypes(flattenArtifacts(paper, ['type_of_artifact']));
}

export function isAnonymized(paper) {
  return !paper.metadata?.title;
}

export const FACETS = [
  {
    key: 'track',
    label: humanize('track'),
    getValues: (p) => (p.metadata?.track ? [p.metadata.track] : []),
  },
  {
    key: 'type_of_inquiry',
    label: humanize('type_of_inquiry'),
    getValues: (p) => p.contribution?.focus_intent?.type_of_inquiry || [],
  },
  {
    key: 'type_of_theory',
    label: humanize('type_of_theory'),
    getValues: (p) => p.contribution?.research_components?.type_of_theory || [],
  },
  {
    key: 'emphasis',
    label: humanize('emphasis'),
    getValues: (p) => p.contribution?.research_components?.emphasis || [],
  },
  {
    key: 'contribution_elements',
    label: 'Contribution Elements',
    getValues: (p) => {
      const c = p.contribution?.research_components || {};
      const out = [];
      if (c.formal_concepts) out.push('formal_concepts');
      if (c.algorithms) out.push('algorithms');
      if (c.hypothesis) out.push('hypothesis');
      return out;
    },
  },
  {
    key: 'type_of_artifact',
    label: humanize('type_of_artifact'),
    getValues: (p) => getArtifactTypes(p),
  },
  {
    key: 'research_method',
    label: humanize('research_method'),
    getValues: (p) => flattenArtifacts(p, ['research_method', 'methods']),
  },
  {
    key: 'evaluation_method',
    label: humanize('evaluation_method'),
    getValues: (p) => flattenArtifacts(p, ['evaluation_method']),
  },
  {
    key: 'data_type',
    label: humanize('data_type'),
    getValues: (p) => flattenArtifacts(p, ['data_collection', 'data_type']),
  },
  {
    key: 'threats_to_validity',
    label: humanize('threats_to_validity'),
    getValues: (p) => flattenArtifacts(p, ['threats_to_validity']),
  },
];

export function getFacetOptions(papers, facet) {
  const counts = {};
  papers.forEach((p) => {
    facet.getValues(p).forEach((v) => {
      counts[v] = (counts[v] || 0) + 1;
    });
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([value, count]) => ({ value, count }));
}

export function matchesFacetFilters(paper, facetFilters) {
  return FACETS.every((facet) => {
    const selected = facetFilters[facet.key];
    if (!selected || selected.length === 0) return true;
    const values = facet.getValues(paper);
    return selected.some((v) => values.includes(v));
  });
}

const EVAL_RANK = {
  none: 0,
  motivating_example: 1,
  argumentation: 1,
  illustrative_example: 1,
  verification: 2,
  case_study: 2,
  interview: 2,
  questionnaire: 2,
  focus_group: 2,
  grounded_theory: 2,
  data_science: 2,
  controlled_experiment: 3,
  field_experiment: 3,
  benchmark: 3,
  technical_experiment: 3,
};
export const RIGOR_LEVELS = ['research', 'emerging', 'established', 'mature'];

export function getEvidenceRigor(paper) {
  const values = flattenArtifacts(paper, ['evaluation_method']);
  const rank = Math.max(0, ...values.map((v) => EVAL_RANK[v] ?? 0));
  return RIGOR_LEVELS[rank];
}
